/**
 * Settings schema (phase 2b.1 P3; Diurnal inventory 06 §5, 02 §8.2; spec docs/specs/basemag-import.md §1).
 *
 * Three layers, later wins for the values it sets: tenant QC defaults (tenant admin), project overrides (manager),
 * user preferences (each person). MB decision 46 (Q19): one default set for files and TA Live, Plot 1 10 min and
 * 10 nT, Plot 2 1 min and 2 nT; no per-format defaults; each person's own changes on the diurnal QC screen are kept
 * (`UserPreferences.diurnal`, docs/specs/diurnal-qc.md §3) and shown in settings. Exports never carry secrets.
 */
import rules from './basemag-rules.json';
import { DEFAULT_PROJECT_OFFSET_H, isValidOffset } from './time-model.js';
import { THOMSON_CONVENTIONS, type TenantConventions } from './conventions.js';
import type { ValidationIssue } from './model.js';

/** QC values a tenant sets as defaults and a project may override. */
export interface QcSettings {
  /** Plot 1 window, minutes, and its threshold, nT. */
  readonly plot1WindowMin: number;
  readonly plot1ThresholdNt: number;
  readonly plot2WindowMin: number;
  readonly plot2ThresholdNt: number;
  /** Minutes hidden at each end of a basemag on the chart and in the QC flags (decision 24). */
  readonly edgeTrimMin: number;
  /** Project UTC offset, hours (decisions 19 and 20). */
  readonly utcOffsetH: number;
  /** "Gradient" (name kept, decision 48): window, minutes, and threshold, nT (Diurnal chart defaults 10 and 10). */
  readonly gradientWindowMin: number;
  readonly gradientThresholdNt: number;
  /** Pcal threshold, nT (first difference × 10). */
  readonly pcalThresholdNt: number;
  /** Deviation of residuals (DoR): window, minutes, and threshold, nT. */
  readonly dorWindowMin: number;
  readonly dorThresholdNt: number;
  /** A live station is online while its newest reading is at most this old, minutes (decision 59, Q32). */
  readonly onlineThresholdMin: number;
}

export type QcKey = keyof QcSettings;

export interface TenantSettings {
  readonly qc: QcSettings;
  readonly conventions: TenantConventions;
}

/**
 * A project's overrides: only the values it changes. `plotMode` is the project's Diurnal QC mode (decision 248):
 * some contracts specify peak to peak instead of the deviation from a chord ("gradient"); absent = from the data
 * format and the contract (`resolveDiurnalSettings`). `show` is the project's Diurnal QC chart defaults (decision
 * 351): which of Plot 2, Gradient, Pcal and DoR the screen shows at first; a chart it leaves out takes the tenant's
 * default (`DEFAULT_VISIBILITY`, every one hidden).
 */
export type ProjectOverrides = Partial<QcSettings> & {
  readonly plotMode?: PlotMode;
  readonly show?: Partial<DiurnalVisibility>;
};

/** Per-person display choices (never limits or offsets). */
export interface UserPreferences {
  readonly theme?: 'dark' | 'light';
  /** Show times in UTC instead of the project offset. */
  readonly showUtc?: boolean;
  /** Last folder a basemag was imported from. */
  readonly lastBasemagFolder?: string;
  /** The person's own diurnal QC screen values (decision 46: kept per person); see `resolveDiurnalSettings`. */
  readonly diurnal?: DiurnalUserPrefs;
}

/** What a person may change for themselves on the diurnal QC screen. */
export interface DiurnalUserPrefs extends Partial<Omit<QcSettings, 'utcOffsetH' | 'onlineThresholdMin'>> {
  /** The mode in effect for the project being resolved (see `userPrefsForProject`). */
  readonly plotMode?: PlotMode;
  /** The person's own mode per project id (decision 248: a project's mode is never overridden elsewhere). */
  readonly plotModeByProject?: Readonly<Record<string, PlotMode>>;
  /** The charts in effect for the project being resolved (see `userPrefsForProject`). */
  readonly show?: Partial<DiurnalVisibility>;
  /**
   * The person's own chart toggles per project id (decision 351: a project's chart defaults are never overridden by
   * a toggle made on another project).
   */
  readonly showByProject?: Readonly<Record<string, Partial<DiurnalVisibility>>>;
  /**
   * Y scale of each metric chart, nT (decision 251; the tool's default 12), in effect for the project being resolved
   * (see `userPrefsForProject`).
   */
  readonly yScale?: Partial<Readonly<Record<YScaleKey, number>>>;
  /**
   * The person's own Y scales per project id (R54: a project's Y scales are its defaults, so a change made on one
   * project never covers another's).
   */
  readonly yScaleByProject?: Readonly<Record<string, Partial<Readonly<Record<YScaleKey, number>>>>>;
  /**
   * The person's own limits per project id (R54: Diurnal QC starts from the project's values, so a limit changed on
   * one project never covers another's).
   */
  readonly qcByProject?: Readonly<
    Record<string, Partial<Omit<QcSettings, 'utcOffsetH' | 'onlineThresholdMin'>>>
  >;
}

/** The metric charts with a Y scale box. */
export type YScaleKey = 'plot1' | 'plot2' | 'gradient' | 'pcal' | 'dor';

/**
 * The person's diurnal values for one project: their mode, chart toggles, limits and Y scales for that project only,
 * never ones set elsewhere. A `show` kept before decision 351, and limits and Y scales kept before R54 (one set for
 * every project), are dropped, so the project's values apply again.
 */
export function userPrefsForProject(prefs: DiurnalUserPrefs, projectId: string): DiurnalUserPrefs {
  const out: Record<string, unknown> = { ...prefs };
  delete out['plotMode'];
  delete out['show'];
  delete out['yScale'];
  for (const k of QC_KEYS) delete out[k];
  Object.assign(out, prefs.qcByProject?.[projectId]);
  const mode = prefs.plotModeByProject?.[projectId];
  if (mode) out['plotMode'] = mode;
  const show = prefs.showByProject?.[projectId];
  if (show && Object.keys(show).length > 0) out['show'] = show;
  const y = prefs.yScaleByProject?.[projectId];
  if (y && Object.keys(y).length > 0) out['yScale'] = y;
  return out as DiurnalUserPrefs;
}

export const PLOT_MODES: readonly PlotMode[] = ['deviationFromChord', 'peakToPeak'];

export function isPlotMode(v: unknown): v is PlotMode {
  return v === 'deviationFromChord' || v === 'peakToPeak';
}

/** Plot 1 and Plot 2 show peak to peak or the deviation from a chord (one mode for both, as in the Diurnal tool). */
export type PlotMode = 'peakToPeak' | 'deviationFromChord';

/** Charts shown; a hidden Plot 2, Gradient, Pcal or DoR raises no flags (decision 47, Q20). Plot 1 always counts. */
export interface DiurnalVisibility {
  readonly plot2: boolean;
  readonly gradient: boolean;
  readonly pcal: boolean;
  readonly dor: boolean;
}

/** The charts a person may show or hide, in the screen's order, with their words. */
export const DIURNAL_CHARTS: ReadonlyArray<{
  readonly key: keyof DiurnalVisibility;
  readonly label: string;
}> = [
  { key: 'plot2', label: 'Plot 2' },
  { key: 'gradient', label: 'Gradient' },
  { key: 'pcal', label: 'Pcal' },
  { key: 'dor', label: 'DoR' },
];

/**
 * The tenant's chart defaults (decision 351): every chart but Plot 1 hidden, for every data format. The Diurnal tool
 * starts with Plot 2 hidden (`ui.js:1959`, its `showPlot2ByDefault` setting false) and puts the visibility back after
 * each import (`processing.js:666-670`), so its auto-show for 862, 823 and MagStation data never lasts.
 */
export const DEFAULT_VISIBILITY: DiurnalVisibility = {
  plot2: false,
  gradient: false,
  pcal: false,
  dor: false,
};

/** Problems with a project's chart defaults (`ProjectOverrides.show`); the field is `show`. */
export function validateChartDefaults(show: Partial<DiurnalVisibility>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const [k, v] of Object.entries(show as Record<string, unknown>)) {
    const chart = DIURNAL_CHARTS.find((c) => c.key === k);
    if (!chart) {
      issues.push({
        field: 'show',
        message: `${k} is not a Diurnal QC chart (Plot 2, Gradient, Pcal or DoR).`,
      });
    } else if (typeof v !== 'boolean') {
      issues.push({ field: 'show', message: `${chart.label} must be shown or hidden.` });
    }
  }
  return issues;
}

export const DEFAULT_QC: QcSettings = {
  plot1WindowMin: 10,
  plot1ThresholdNt: 10,
  plot2WindowMin: 1,
  plot2ThresholdNt: 2,
  edgeTrimMin: rules.edgeTrimDefaultMin,
  utcOffsetH: DEFAULT_PROJECT_OFFSET_H,
  gradientWindowMin: 10,
  gradientThresholdNt: 10,
  pcalThresholdNt: 10,
  dorWindowMin: 10,
  dorThresholdNt: 10,
  onlineThresholdMin: 15,
};

export const THOMSON_SETTINGS: TenantSettings = { qc: DEFAULT_QC, conventions: THOMSON_CONVENTIONS };

/**
 * A project's hours from UTC: its own `utcOffsetH` override, else the default (decisions 19 and 20). What Diurnal QC,
 * Space weather and the top bar's clocks (R142) show times in.
 */
export function projectUtcOffsetH(project: {
  readonly qc_overrides?: Readonly<Record<string, unknown>> | null;
}): number {
  const v = project.qc_overrides?.['utcOffsetH'];
  return typeof v === 'number' && Number.isFinite(v) ? v : DEFAULT_QC.utcOffsetH;
}

export type SettingSource = 'tenant' | 'project';

/**
 * Where a setting's value comes from, in the words people read (MB's R68): the company's own default ("tenant" in
 * code), or the project's. Every screen, help page and message that shows a value's source uses these words, never
 * "Tenant default".
 */
export const SETTING_SOURCE_LABEL: Readonly<Record<SettingSource, string>> = {
  tenant: 'Company default',
  project: 'Project default',
};

export function settingSourceLabel(source: SettingSource): string {
  return SETTING_SOURCE_LABEL[source];
}

export interface ResolvedSetting<T> {
  readonly value: T;
  readonly source: SettingSource;
}

export type ResolvedQc = { readonly [K in QcKey]: ResolvedSetting<QcSettings[K]> };

export const QC_KEYS: readonly QcKey[] = [
  'plot1WindowMin',
  'plot1ThresholdNt',
  'plot2WindowMin',
  'plot2ThresholdNt',
  'edgeTrimMin',
  'utcOffsetH',
  'gradientWindowMin',
  'gradientThresholdNt',
  'pcalThresholdNt',
  'dorWindowMin',
  'dorThresholdNt',
  'onlineThresholdMin',
];

/** Effective QC values for a project, each with where it came from. */
export function resolveQcSettings(
  tenant: QcSettings,
  project: ProjectOverrides | null | undefined,
): ResolvedQc {
  const out = {} as Record<QcKey, ResolvedSetting<number>>;
  for (const k of QC_KEYS) {
    const v = project?.[k];
    out[k] =
      v === undefined || v === null
        ? { value: tenant[k], source: 'tenant' }
        : { value: v, source: 'project' };
  }
  return out as ResolvedQc;
}

/** The plain values of `resolveQcSettings`. */
export function effectiveQc(tenant: QcSettings, project: ProjectOverrides | null | undefined): QcSettings {
  const r = resolveQcSettings(tenant, project);
  return Object.fromEntries(QC_KEYS.map((k) => [k, r[k].value])) as unknown as QcSettings;
}

/** Problems with QC values (tenant defaults or a project's overrides); field names are the keys. */
export function validateQcSettings(s: Partial<QcSettings>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const positive: ReadonlyArray<[QcKey, string]> = [
    ['plot1WindowMin', 'The Plot 1 window must be more than 0 min.'],
    ['plot1ThresholdNt', 'The Plot 1 threshold must be more than 0 nT.'],
    ['plot2WindowMin', 'The Plot 2 window must be more than 0 min.'],
    ['plot2ThresholdNt', 'The Plot 2 threshold must be more than 0 nT.'],
    ['gradientWindowMin', 'The Gradient window must be more than 0 min.'],
    ['gradientThresholdNt', 'The Gradient threshold must be more than 0 nT.'],
    ['pcalThresholdNt', 'The Pcal threshold must be more than 0 nT.'],
    ['dorWindowMin', 'The DoR window must be more than 0 min.'],
    ['dorThresholdNt', 'The DoR threshold must be more than 0 nT.'],
    ['onlineThresholdMin', 'The online threshold must be more than 0 min.'],
  ];
  for (const [k, message] of positive) {
    const v = s[k];
    if (v !== undefined && !(Number.isFinite(v) && v > 0)) issues.push({ field: k, message });
  }
  const trim = s.edgeTrimMin;
  if (trim !== undefined && !(Number.isFinite(trim) && trim >= 0 && trim <= rules.edgeTrimMaxMin)) {
    issues.push({ field: 'edgeTrimMin', message: `The edge trim must be 0 to ${rules.edgeTrimMaxMin} min.` });
  }
  const off = s.utcOffsetH;
  if (off !== undefined && !isValidOffset(off)) {
    issues.push({
      field: 'utcOffsetH',
      message: 'The UTC offset must be −12 to +14 h in steps of 15 minutes (0 is allowed).',
    });
  }
  return issues;
}

/**
 * The project's Diurnal QC defaults set in Project settings (MB's R54), in the panel's order: null where the company
 * default (or the contract) applies. The nT and min values are `qc_overrides` keys; the two Y scales are project
 * columns (Teslyn Field 2026.10.6 refuses an overrides save holding a key it does not know).
 */
export interface DiurnalProjectDefaults {
  readonly gradientThresholdNt: number | null;
  readonly gradientWindowMin: number | null;
  readonly plot1ThresholdNt: number | null;
  readonly plot1WindowMin: number | null;
  readonly edgeTrimMin: number | null;
  readonly plot1YScaleNt: number | null;
  readonly gradientYScaleNt: number | null;
}

/** The fields of the panel, in order, with their words. */
export const DIURNAL_PROJECT_FIELDS: ReadonlyArray<{
  readonly key: keyof DiurnalProjectDefaults;
  readonly label: string;
  readonly unit: 'nT' | 'min';
}> = [
  { key: 'gradientThresholdNt', label: 'Gradient (nT)', unit: 'nT' },
  { key: 'gradientWindowMin', label: 'Gradient window (min)', unit: 'min' },
  { key: 'plot1ThresholdNt', label: 'Plot 1 (nT)', unit: 'nT' },
  { key: 'plot1WindowMin', label: 'Plot 1 window (min)', unit: 'min' },
  { key: 'edgeTrimMin', label: 'Edge trim (min)', unit: 'min' },
  { key: 'plot1YScaleNt', label: 'Plot 1 Y scale (nT)', unit: 'nT' },
  { key: 'gradientYScaleNt', label: 'Gradient Y scale (nT)', unit: 'nT' },
];

const OVERRIDE_KEYS = [
  'gradientThresholdNt',
  'gradientWindowMin',
  'plot1ThresholdNt',
  'plot1WindowMin',
  'edgeTrimMin',
] as const;

export function diurnalProjectDefaults(project: {
  readonly qc_overrides?: unknown;
  readonly diurnal_plot1_y_scale_nt?: number | null;
  readonly diurnal_gradient_y_scale_nt?: number | null;
}): DiurnalProjectDefaults {
  const o = (project.qc_overrides ?? {}) as Record<string, unknown>;
  const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const out: Record<string, number | null> = {};
  for (const k of OVERRIDE_KEYS) out[k] = num(o[k]);
  out['plot1YScaleNt'] = num(project.diurnal_plot1_y_scale_nt);
  out['gradientYScaleNt'] = num(project.diurnal_gradient_y_scale_nt);
  return out as unknown as DiurnalProjectDefaults;
}

/**
 * The project's overrides with the panel's five nT and min values put in (a null one removed, so the company
 * default applies); every other override (offset, mode, charts shown, Plot 2 ...) is kept.
 */
export function withDiurnalProjectDefaults(
  overrides: ProjectOverrides | null | undefined,
  d: Partial<DiurnalProjectDefaults>,
): ProjectOverrides {
  const next: Record<string, unknown> = { ...overrides };
  for (const k of OVERRIDE_KEYS) {
    if (!(k in d)) continue;
    const v = d[k];
    if (v === null || v === undefined) delete next[k];
    else next[k] = v;
  }
  return next as ProjectOverrides;
}

/** Problems with the panel's values; the field names are the keys. Blank (null) is always fine. */
export function validateDiurnalProjectDefaults(d: Partial<DiurnalProjectDefaults>): ValidationIssue[] {
  const qc: Partial<Record<QcKey, number>> = {};
  for (const k of OVERRIDE_KEYS) {
    const v = d[k];
    if (typeof v === 'number') qc[k] = v;
  }
  const issues = validateQcSettings(qc);
  for (const [k, label] of [
    ['plot1YScaleNt', 'The Plot 1 Y scale'],
    ['gradientYScaleNt', 'The Gradient Y scale'],
  ] as const) {
    const v = d[k];
    if (v !== null && v !== undefined && !(Number.isFinite(v) && v > 0))
      issues.push({ field: k, message: `${label} must be more than 0 nT.` });
  }
  const order: string[] = DIURNAL_PROJECT_FIELDS.map((f) => f.key);
  return issues.sort((a, b) => order.indexOf(a.field) - order.indexOf(b.field));
}

/** Keys that look like secrets; never written to an export and refused on import. */
const SECRET_KEY = /(secret|token|password|passwd|api[-_]?key|apikey|credential|private[-_]?key)/i;

export function isSecretKey(key: string): boolean {
  return SECRET_KEY.test(key);
}

function withoutSecrets(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withoutSecrets);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([k]) => !isSecretKey(k))
        .map(([k, v]) => [k, withoutSecrets(v)]),
    );
  }
  return value;
}

export interface SettingsExport {
  readonly format: 'teslyn-settings';
  readonly version: 1;
  readonly tenant?: TenantSettings;
  readonly project?: ProjectOverrides;
  readonly user?: UserPreferences;
}

/** The settings layers as a JSON text a person can keep or send to support; secret-looking keys are left out. */
export function exportSettings(layers: Omit<SettingsExport, 'format' | 'version'>): string {
  const doc = withoutSecrets({ format: 'teslyn-settings', version: 1, ...layers });
  return `${JSON.stringify(doc, null, 2)}\n`;
}

function secretPaths(value: unknown, path: string, out: string[]): void {
  if (value !== null && typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const p = path ? `${path}.${k}` : k;
      if (isSecretKey(k)) out.push(p);
      secretPaths(v, p, out);
    }
  }
}

/** Read an export back; refuses a file that is not a Teslyn settings export or that holds a secret-looking key. */
export function importSettings(text: string): SettingsExport {
  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch {
    throw new Error('This file is not a Teslyn settings export (it is not valid JSON).');
  }
  const d = doc as Partial<SettingsExport> | null;
  // 'survera-settings': the format name of files exported before the product was renamed (decision 529)
  const format = d?.format as string | undefined;
  if (!d || (format !== 'teslyn-settings' && format !== 'survera-settings') || d.version !== 1) {
    throw new Error('This file is not a Teslyn settings export.');
  }
  const secrets: string[] = [];
  secretPaths(doc, '', secrets);
  if (secrets.length > 0) {
    throw new Error(
      `Settings exports never hold keys or passwords; remove ${secrets.join(', ')} and try again.`,
    );
  }
  const issues = [...validateQcSettings(d.tenant?.qc ?? {}), ...validateQcSettings(d.project ?? {})];
  if (issues.length > 0) throw new Error(issues.map((i) => i.message).join(' '));
  return d as SettingsExport;
}
